import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import {
  IsOwnMatchHistory,
  MatchHistoryGame,
  MatchHistoryplayerToSearchFor,
} from "MVVM/State/matchHistory";
import { AllstarUser } from "MVVM/State";

import { getPlayerGameIdByGame, getPlayerSearchIdentifier } from "./utils";
import { MATCHES, STEAM_FRIENDS } from "../../GraphQL/matchHistory";

const PAGE_SIZE = 10;

export const MatchesViewmodel = (
  pageSize: number = PAGE_SIZE,
  playerGameIdentifier?: string,
  skip: boolean = false,
) => {
  const { hasEpicLinked, hasRiotLinked } = useLinkedAccounts();
  const [matchHistoryPlayerToSearchFor, setMatchHistoryPlayerToSearchFor] =
    useRecoilState(MatchHistoryplayerToSearchFor);
  const setIsOwnMatchHistory = useSetRecoilState(IsOwnMatchHistory);
  const allstarUser = useRecoilValue(AllstarUser);
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const playerIdFromUrl = getPlayerSearchIdentifier();

  const {
    data: matchesData,
    error: matchesError,
    loading: matchesLoading,
    fetchMore,
  } = useQuery(MATCHES, {
    variables: {
      playerGameId: matchHistoryPlayerToSearchFor,
      first: pageSize,
    },
    skip: (!matchHistoryPlayerToSearchFor && !playerIdFromUrl) || skip,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  useEffect(() => {
    const playerToSearchFor =
      playerIdFromUrl ||
      playerGameIdentifier ||
      getPlayerGameIdByGame(matchHistoryGame, allstarUser);
    setMatchHistoryPlayerToSearchFor(playerToSearchFor);
  }, [
    allstarUser,
    matchHistoryGame,
    setMatchHistoryPlayerToSearchFor,
    matchHistoryPlayerToSearchFor,
    playerIdFromUrl,
    playerGameIdentifier,
  ]);

  useEffect(() => {
    const ourGameId = getPlayerGameIdByGame(matchHistoryGame, allstarUser);
    setIsOwnMatchHistory(ourGameId === matchHistoryPlayerToSearchFor);
  }, [
    matchHistoryPlayerToSearchFor,
    allstarUser,
    matchHistoryGame,
    setIsOwnMatchHistory,
  ]);

  const handleFetchMore = async () => {
    await fetchMore({
      variables: {
        after: matchesData?.matches?.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          matches: {
            ...fetchMoreResult.matches,
            nodes: [
              ...(prev.matches?.nodes || []),
              ...(fetchMoreResult.matches?.nodes || []),
            ],
          },
        };
      },
    });
  };

  return {
    matches: matchesData?.matches?.nodes || [],
    matchesLoading,
    matchesError,
    noEpicLinked: !hasEpicLinked,
    noRiotLinked: !hasRiotLinked,
    matchHistoryPlayerToSearchFor,
    fetchMore: handleFetchMore,
    pageInfo: matchesData?.matches?.pageInfo,
  };
};

export const SteamFriendsViewModel = () => {
  const { hasSteamLinked } = useLinkedAccounts();
  const { data: friendsData, loading: friendsLoading } = useQuery(
    STEAM_FRIENDS,
    {
      skip: !hasSteamLinked,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    friendsData: friendsData?.steamFriends ?? [],
    friendsLoading,
  };
};
